/**
 * Auto-Cycling Features Section Component
 * 
 * A responsive features section that displays 4 features in full-width cards.
 * Desktop: Auto-cycles through features every 3 seconds with a timer bar,
 * manual navigation controls, and clickable feature names.
 * Mobile: Horizontal scrollable cards with background blend effects,
 * no auto-cycling or timer.
 */

'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import {
  ClockIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  SparklesIcon,
  CodeBracketIcon,
  ChartBarIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  RocketLaunchIcon,
  DevicePhoneMobileIcon,
  GlobeAltIcon,
  EnvelopeIcon,
  CalendarIcon,
  BanknotesIcon,
  LockClosedIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import ScrollReveal from '../ui/ScrollReveal';

interface FeatureHighlight {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface Feature {
  title: string;
  shortDescription: string;
  description: string;
  image: string;
  highlights: FeatureHighlight[];
}

const features: Feature[] = [
  {
    title: 'Professionally Built Website',
    shortDescription: 'A professionally built website crafted specifically for your business',
    description: 'A professionally built website crafted specifically for your business. Every element is thoughtfully designed and developed to reflect your brand identity and meet your unique needs. No templates, no compromises— this is the root of your digital presence.',
    image: '/images/marketing/SiteBuilderLineArt.png',
    highlights: [ 
      {
        icon: <RocketLaunchIcon className="w-5 h-5" />,
        title: 'Performance Optimization',
        description: 'Optimized for speed, stop losing customers to slow loading times'
      },
      {
        icon: <ShieldCheckIcon className="w-5 h-5" />,
        title: 'Wide Availability Hosting',
        description: 'We work to ensure your website is available anywhere, anytime'
      },
      {
        icon: <GlobeAltIcon className="w-5 h-5" />,
        title: 'SEO Optimized',
        description: 'Built to rank well in search results so you are naturally found'
      }
    ]
  },
  {
    title: 'Online Payment Handling',
    shortDescription: 'Secure payment processing made simple',
    description: 'Accept payments online with confidence. Our integrated payment solutions provide secure, seamless transactions for your customers while giving you complete control over your financial operations. Set up in minutes, not days.',
    image: '/images/marketing/payment_processing.png',
    highlights: [
      {
        icon: <CreditCardIcon className="w-5 h-5" />,
        title: 'Multiple Payment Methods',
        description: 'Accept cards, digital wallets, pay later methods, and more'
      },
      {
        icon: <LockClosedIcon className="w-5 h-5" />,
        title: 'Bank-Level Security',
        description: 'PCI compliant with encrypted transactions'
      },
      {
        icon: <BanknotesIcon className="w-5 h-5" />,
        title: 'Instant Processing',
        description: 'Real-time payment processing and confirmations'
      },
      {
        icon: <DevicePhoneMobileIcon className="w-5 h-5" />,
        title: 'Mobile Payments',
        description: 'Accept payments anywhere, on any device'
      }
    ]
  },
  {
    title: 'Online Marketing Automation',
    shortDescription: 'Automate your marketing campaigns effortlessly',
    description: 'Streamline your marketing efforts with intelligent automation tools. From email campaigns to social media scheduling, our platform helps you reach customers at the right time with the right message.',
    image: '/images/marketing/MarketingAutomation_LineArt.png',
    highlights: [
      {
        icon: <EnvelopeIcon className="w-5 h-5" />,
        title: 'Auto-Campaigns',
        description: 'You tell us what you want to say, and we tell the world for you'
      },
      {
        icon: <ChartBarIcon className="w-5 h-5" />,
        title: 'Analytics Dashboard',
        description: 'Track performance and measure campaign success right on your dashboard'
      },
      {
        icon: <CalendarIcon className="w-5 h-5" />,
        title: 'Campaign Scheduling',
        description: 'Schedule and manage campaigns to run at the best time for your business'
      },
      {
        icon: <BoltIcon className="w-5 h-5" />,
        title: 'Follow-through Reporting',
        description: 'See exactly how much a campaign has earned you, automatically'
      }
    ]
  },
  {
    title: 'AI Chatbot',
    shortDescription: 'Intelligent chatbots that for you and your customers',
    description: 'Never miss a lead with AI-powered chatbots that answer questions, capture leads, and guide customers through their journey—all automatically. Your business is now always connected.',
    image: '/images/marketing/chatbot.png',
    highlights: [
      {
        icon: <ClockIcon className="w-5 h-5" />,
        title: '24/7 Availability',
        description: 'Never miss a lead with instant responses around the clock'
      },
      {
        icon: <ChatBubbleLeftRightIcon className="w-5 h-5" />,
        title: 'Natural Conversations',
        description: 'AI-powered chats that feel human and engaging to interact with'
      },
      {
        icon: <UserGroupIcon className="w-5 h-5" />,
        title: 'Lead Capture',
        description: 'Automatically collect and qualify leads from conversations'
      },
      {
        icon: <SparklesIcon className="w-5 h-5" />,
        title: 'Business Knowledge',
        description: 'Your chatbot is built specifically for your business'
      }
    ]
  }
];

const CYCLE_DURATION = 5000; // 3 seconds

export default function FeaturesSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [timerProgress, setTimerProgress] = useState(0);
  const [mobileActiveIndex, setMobileActiveIndex] = useState(0);
  const [mobileTimerProgress, setMobileTimerProgress] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const pausedAtRef = useRef<number | null>(null);
  const mobileAnimationFrameRef = useRef<number | null>(null);
  const mobileStartTimeRef = useRef<number>(Date.now());
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Track mount state
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Reset timer
  const resetTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    setTimerProgress(0);
    pausedAtRef.current = null;
    startTimeRef.current = Date.now();
  };

  // Reset timer when activeIndex changes
  useEffect(() => {
    if (!isMounted) return;
    
    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
    if (!isDesktop) {
      return;
    }

    // Only reset when activeIndex changes
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    setTimerProgress(0);
    pausedAtRef.current = null;
    startTimeRef.current = Date.now();
  }, [activeIndex, isMounted]);

  // Auto-cycle logic (desktop only)
  useEffect(() => {
    if (!isMounted) return;
    
    // Only run auto-cycle on desktop (lg breakpoint and above)
    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
    if (!isDesktop) {
      return;
    }

    // Update progress bar using requestAnimationFrame
    const updateProgress = () => {
      if (isPaused) {
        // When pausing, save the current timestamp and current progress if not already saved
        if (pausedAtRef.current === null) {
          pausedAtRef.current = Date.now();
          // Calculate and set current progress before pausing
          const elapsed = Date.now() - startTimeRef.current;
          const currentProgress = Math.min((elapsed / CYCLE_DURATION) * 100, 100);
          setTimerProgress(currentProgress);
        }
        // Keep the loop running to detect when resume happens
        animationFrameRef.current = requestAnimationFrame(updateProgress);
        return;
      }

      // When resuming, adjust start time to account for pause duration
      if (pausedAtRef.current !== null) {
        const pauseDuration = Date.now() - pausedAtRef.current;
        startTimeRef.current += pauseDuration;
        pausedAtRef.current = null;
      }

      const elapsed = Date.now() - startTimeRef.current;
      const progress = Math.min((elapsed / CYCLE_DURATION) * 100, 100);
      setTimerProgress(progress);

      if (progress < 100) {
        animationFrameRef.current = requestAnimationFrame(updateProgress);
      } else {
        // Auto-cycle to next feature when progress reaches 100%
        setActiveIndex((prev) => (prev + 1) % features.length);
      }
    };

    // Always start the animation loop
    animationFrameRef.current = requestAnimationFrame(updateProgress);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [activeIndex, isPaused, isMounted]);

  // Handle manual navigation
  const goToFeature = (index: number) => {
    setActiveIndex(index);
    resetTimer();
  };

  const goToPrevious = () => {
    setActiveIndex((prev) => (prev - 1 + features.length) % features.length);
    resetTimer();
  };

  const goToNext = () => {
    setActiveIndex((prev) => (prev + 1) % features.length);
    resetTimer();
  };

  // Keyboard navigation
  useEffect(() => {
    if (!isMounted) return;
    
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only handle keyboard navigation on desktop
      const isDesktop = window.innerWidth >= 1024;
      if (!isDesktop) return;
      
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isMounted]);

  // Mobile scroll tracking
  useEffect(() => {
    if (!isMounted) return;
    
    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
    if (isDesktop || !scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    
    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const cardWidth = container.scrollWidth / features.length;
      const newIndex = Math.round(scrollLeft / cardWidth);
      const clampedIndex = Math.max(0, Math.min(newIndex, features.length - 1));
      
      if (clampedIndex !== mobileActiveIndex) {
        setMobileActiveIndex(clampedIndex);
        setMobileTimerProgress(0);
        mobileStartTimeRef.current = Date.now();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isMounted, mobileActiveIndex]);

  // Mobile auto-cycle timer
  useEffect(() => {
    if (!isMounted) return;
    
    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
    if (isDesktop) return;

    const updateMobileProgress = () => {
      const elapsed = Date.now() - mobileStartTimeRef.current;
      const progress = Math.min((elapsed / CYCLE_DURATION) * 100, 100);
      setMobileTimerProgress(progress);

      if (progress < 100) {
        mobileAnimationFrameRef.current = requestAnimationFrame(updateMobileProgress);
      } else {
        // Auto-cycle to next feature
        const nextIndex = (mobileActiveIndex + 1) % features.length;
        setMobileActiveIndex(nextIndex);
        setMobileTimerProgress(0);
        mobileStartTimeRef.current = Date.now();
        
        // Scroll to next card
        if (scrollContainerRef.current) {
          const cardWidth = scrollContainerRef.current.scrollWidth / features.length;
          scrollContainerRef.current.scrollTo({
            left: nextIndex * cardWidth,
            behavior: 'smooth'
          });
        }
      }
    };

    setMobileTimerProgress(0);
    mobileStartTimeRef.current = Date.now();
    mobileAnimationFrameRef.current = requestAnimationFrame(updateMobileProgress);

    return () => {
      if (mobileAnimationFrameRef.current) {
        cancelAnimationFrame(mobileAnimationFrameRef.current);
      }
    };
  }, [mobileActiveIndex, isMounted]);

  const activeFeature = features[activeIndex];

  return (
    <section className="bg-white py-12 lg:py-16">
      {/* Desktop Layout */}
      <div className="hidden lg:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="text-left mb-12">
            <h2 className="font-poppins text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Everything you need to succeed online
            </h2>
            <p className="font-poppins text-base sm:text-lg text-gray-600 max-w-3xl leading-relaxed">
              From custom websites to payment processing, we provide comprehensive digital solutions that help small businesses thrive in today's competitive marketplace.
            </p>
          </div>

          {/* Full-Width Card */}
          <ScrollReveal delay={500}>
            <div
              className="border border-gray-200 bg-white rounded-lg shadow-sm overflow-hidden"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div className="grid lg:grid-cols-2 gap-0">
                {/* Text Content */}
                <div className="p-4 lg:p-6 flex flex-col justify-center">
                  <h3 className="font-poppins text-xl lg:text-3xl font-bold text-gray-900 mb-3">
                    {activeFeature.title}
                  </h3>
                  <p className="font-poppins text-sm text-gray-600 leading-relaxed mb-6 h-24 line-clamp-4">
                    {activeFeature.description}
                  </p>
                  
                  {/* Feature Highlights Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {activeFeature.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <div className="text-gray-900 mt-0.5 flex-shrink-0">
                          {highlight.icon}
                        </div>
                        <div>
                          <h4 className="font-poppins text-sm font-semibold text-gray-900 mb-1">
                            {highlight.title}
                          </h4>
                          <p className="font-poppins text-xs text-gray-600 leading-relaxed line-clamp-2">
                            {highlight.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    className="self-start px-6 py-2 border border-gray-900 text-gray-900 font-poppins text-sm font-medium rounded hover:bg-gray-900 hover:text-white transition-colors"
                    aria-label={`Learn more about ${activeFeature.title}`}
                  >
                    Learn More
                  </button>
                </div>

                {/* Image */}
                <div className="relative h-64 lg:h-auto lg:min-h-[400px]">
                  <Image
                    src={activeFeature.image}
                    alt={activeFeature.title}
                    fill
                    className="object-cover"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                  />
                </div>
              </div>
            </div>

            {/* Feature Names Row */}
            <div className="flex items-start gap-6 py-4 mt-4">
              {features.map((feature, index) => (
                <button
                  key={index}
                  onClick={() => goToFeature(index)}
                  onMouseEnter={() => setIsPaused(true)}
                  onMouseLeave={() => setIsPaused(false)}
                  className="relative flex flex-col items-center text-left flex-1"
                  aria-label={`View ${feature.title}`}
                >
                  {/* Timer Bar - only visible for active feature, positioned directly above the text */}
                  {index === activeIndex && (
                    <div className="absolute -top-0.5 left-0 right-0 h-0.5">
                      <div 
                        className="h-full bg-gray-900 transition-none"
                        style={{ width: `${timerProgress}%` }}
                      />
                    </div>
                  )}
                  <div
                    className={`font-poppins transition-colors p-2 w-full h-24 ${
                      index === activeIndex
                        ? 'bg-gray-100 rounded-b'
                        : 'bg-white rounded hover:bg-gray-50'
                    }`}
                  >
                    <span
                      className={`text-base font-medium block ${
                        index === activeIndex
                          ? 'text-gray-900'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {feature.title}
                    </span>
                    <span className="text-xs text-gray-600 block mt-2 pb-2">
                      {feature.shortDescription}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden px-4 my-auto">
        <h1 className="font-poppins text-2xl sm:text-3xl font-bold text-gray-900 mb-4">What We Offer</h1>
        <div 
          ref={scrollContainerRef}
          className="overflow-x-scroll snap-x snap-mandatory scrollbar-hide"
        >
          <div className="flex gap-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="w-full flex-shrink-0 snap-center px-4 sm:px-6"
              >
                {/* Background Blend Effect */}
                <div className="relative -mt-8 -mx-4 sm:-mx-6 pt-8 pb-12 bg-gradient-to-b from-white via-white/95 to-white">
                  {/* Card */}
                  <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden relative">
                    {/* Text Content */}
                    <div className="p-4">
                      <h3 className="font-poppins text-lg font-bold text-gray-900 mb-3 flex items-center justify-between">
                        <span>{feature.title}</span>
                        <span className="font-normal text-gray-400 text-sm ml-2">
                          {index + 1}/{features.length}
                        </span>
                      </h3>
                      <p className="font-poppins text-xs text-gray-600 leading-relaxed mb-4 min-h-[4rem] line-clamp-4">
                        {feature.description}
                      </p>

                      <button
                        className="px-6 py-2 border border-gray-900 text-gray-900 font-poppins text-sm font-medium rounded hover:bg-gray-900 hover:text-white transition-colors"
                        aria-label={`Learn more about ${feature.title}`}
                      >
                        Learn More
                      </button>
                    </div>

                    {/* Image */}
                    <div className="relative h-64 w-full">
                      <Image
                        src={feature.image}
                        alt={feature.title}
                        fill
                        className="object-cover"
                        sizes="100vw"
                      />
                    </div>

                    {/* Timer Bar - Top of Card */}
                    {index === mobileActiveIndex && (
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200">
                        <div 
                          className="h-full bg-gray-900 transition-none"
                          style={{ width: `${mobileTimerProgress}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx global>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        @keyframes timerProgress {
          from {
            width: 0%;
          }
          to {
            width: 100%;
          }
        }
      `}</style>
    </section>
  );
}

